<template lang="html">
  <section>
    <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title has-text-left">Edit Step</p>
          <b-button icon-left="delete" type="is-danger" @click="deleteStep"
            >Delete step</b-button
          >
        </header>
        <section class="modal-card-body">
          <b-notification
            @close="resetErrors"
            :active="updateStepErrors != null"
            type="is-danger"
            has-icon
            aria-close-label="Close notification"
            role="alert"
          >
            {{ updateStepErrors }}
          </b-notification>

          <b-tabs position="is-centered" class="block">
            <!-- Edit Step -->
            <b-tab-item label="Step">
              <ValidationProvider
                slim
                rules="required"
                name="Title"
                v-slot="{ errors, valid }"
              >
                <b-field
                  :type="{ 'is-danger': errors[0], 'is-success': valid }"
                  :message="errors"
                >
                  <template slot="label">
                    Step Title
                    <b-tooltip
                      type="is-dark"
                      multilined
                      size="is-small"
                      position="is-right"
                      label="Add a title for the step."
                    >
                      <b-icon
                        type="is-gray"
                        size="is-small"
                        icon="help-circle-outline"
                      ></b-icon>
                    </b-tooltip>
                  </template>
                  <b-input
                    placeholder="e.g. Glue on the prop"
                    v-model="editingStep.title"
                    maxlength="50"
                  ></b-input>
                </b-field>
              </ValidationProvider>

              <b-field>
                <template slot="label">
                  Step description
                  <b-tooltip
                    type="is-dark"
                    multilined
                    size="is-small"
                    position="is-right"
                    label="Add a description on how to perform this step."
                  >
                    <b-icon
                      type="is-gray"
                      size="is-small"
                      icon="help-circle-outline"
                    ></b-icon>
                  </b-tooltip>
                </template>
                <b-input
                  maxlength="1000"
                  type="textarea"
                  placeholder="e.g. Glue on the prop"
                  v-model="editingStep.description"
                ></b-input>
              </b-field>

              <b-field class="has-text-centered">
                <template slot="label">
                  Edit Step images
                  <b-tooltip
                    type="is-dark"
                    multilined
                    size="is-small"
                    position="is-right"
                    label="Add images to illustrate your step."
                  >
                    <b-icon
                      type="is-gray"
                      size="is-small"
                      icon="help-circle-outline"
                    ></b-icon>
                  </b-tooltip>
                </template>
                <image-management
                  class="has-text-left"
                  :imageable-id="step.id"
                  :imageable-type="'step'"
                  @imagesUpdated="imagesUpdated"
                />
              </b-field>
            </b-tab-item>

            <!-- Edit Hint -->
            <b-tab-item label="Hint">
              <b-field>
                <template slot="label">
                  Hint description
                  <b-tooltip
                    type="is-dark"
                    multilined
                    size="is-small"
                    position="is-right"
                    label="Add a hint that could help the student to better perform this step."
                  >
                    <b-icon
                      type="is-gray"
                      size="is-small"
                      icon="help-circle-outline"
                    ></b-icon>
                  </b-tooltip>
                </template>
                <b-input
                  maxlength="200"
                  type="textarea"
                  placeholder="Bend the paper this way !"
                  v-model="editingStep.hint"
                ></b-input>
              </b-field>

              <div class="pb-1">
                <span class="has-text-weight-semibold">Hint image </span>
                <b-tooltip
                  type="is-dark"
                  multilined
                  size="is-small"
                  position="is-right"
                  label="Add an image to illustrate your hint described above."
                >
                  <b-icon
                    type="is-gray"
                    size="is-small"
                    icon="help-circle-outline"
                  ></b-icon>
                </b-tooltip>
              </div>
              <div
                v-if="editingStep.hint_image || step.attributes.hint_image.url"
                class="columns is-centered"
              >
                <div class="column is-8">
                  <figure v-if="editingStep.hint_image" class="image is-1by1">
                    <img :src="editingStep.hint_image" alt="Placeholder image" />
                  </figure>

                  <figure
                    v-else-if="step.attributes.hint_image.url"
                    class="image is-1by1"
                  >
                    <img :src="step.attributes.hint_image.url" alt="" />
                  </figure>
                </div>
              </div>

              <div class="has-text-centered">
                <div v-if="step.attributes.hint_image.url || editingStep.hint_image">
                  <span class="p-1">
                    <b-button
                      icon-left="pencil"
                      type="is-success"
                      @click="isHintImageModalActive = true"
                    >
                      Edit image
                    </b-button>
                  </span>
                  <span class="p-1">
                    <b-button
                      icon-left="delete"
                      type="is-danger"
                      @click="deleteHintImage"
                    >
                      Delete image
                    </b-button>
                  </span>
                </div>

                <span v-else>
                  <b-button
                    icon-left="plus"
                    type="is-success"
                    @click="isHintImageModalActive = true"
                  >
                    Add an image
                  </b-button>
                </span>
              </div>

              <br />
            </b-tab-item>

            <!-- Edit Epic Fail -->

            <b-tab-item label="Epic fail">
              <b-field>
                <template slot="label">
                  Epic fail description
                  <b-tooltip
                    type="is-dark"
                    multilined
                    size="is-small"
                    position="is-right"
                    label="Add an epic fail to show that nothing is perfect at first try."
                  >
                    <b-icon
                      type="is-gray"
                      size="is-small"
                      icon="help-circle-outline"
                    ></b-icon>
                  </b-tooltip>
                </template>
                <b-input
                  maxlength="200"
                  type="textarea"
                  placeholder="Oops, I did it wrong!"
                  v-model="editingStep.epic_fail"
                ></b-input>
              </b-field>

              <div class="pb-1">
                <span class="has-text-weight-semibold">Epic fail image </span>
                <b-tooltip
                  type="is-dark"
                  multilined
                  size="is-small"
                  position="is-right"
                  label="Add an image to illustrate your proud failure."
                >
                  <b-icon
                    type="is-gray"
                    size="is-small"
                    icon="help-circle-outline"
                  ></b-icon>
                </b-tooltip>
              </div>
              <div
                v-if="editingStep.epic_fail_image || step.attributes.epic_fail_image.url"
                class="columns is-centered"
              >
                <div class="column is-8">
                  <figure v-if="editingStep.epic_fail_image" class="image is-1by1">
                    <img :src="editingStep.epic_fail_image" alt="Placeholder image" />
                  </figure>

                  <figure
                    v-else-if="step.attributes.epic_fail_image.url"
                    class="image is-1by1"
                  >
                    <img :src="step.attributes.epic_fail_image.url" alt="" />
                  </figure>
                </div>
              </div>
              <div class="has-text-centered">
                <div
                  v-if="
                    step.attributes.epic_fail_image.url || editingStep.epic_fail_image
                  "
                >
                  <span class="p-1">
                    <b-button
                      icon-left="pencil"
                      type="is-success"
                      @click="isEpicFailImageModalActive = true"
                    >
                      Edit image
                    </b-button>
                  </span>
                  <span class="p-1">
                    <b-button
                      icon-left="delete"
                      type="is-danger"
                      @click="deleteEpicFailImage"
                    >
                      Delete image
                    </b-button>
                  </span>
                </div>

                <span v-else>
                  <b-button
                    icon-left="plus"
                    type="is-success"
                    @click="isEpicFailImageModalActive = true"
                  >
                    Add an image
                  </b-button>
                </span>
              </div>

              <br />
            </b-tab-item>
          </b-tabs>
        </section>

        <footer class="modal-card-foot">
          <span class="card-footer-item">
            <b-button type="is-primary" @click="handleSubmit(updateStep)">Save</b-button>
            <b-button @click="$parent.close()"> Close </b-button>
          </span>
        </footer>
      </div>
    </ValidationObserver>

    <b-modal
      :active.sync="isHintImageModalActive"
      has-modal-card
      trap-focus
      aria-role="dialog"
      aria-modal
    >
      <image-uploader
        :title="'Upload Hint Images'"
        :emitSignal="'hintImageUploaded'"
        @hintImageUploaded="updateHintImage"
      />
    </b-modal>

    <b-modal
      :active.sync="isEpicFailImageModalActive"
      has-modal-card
      trap-focus
      aria-role="dialog"
      aria-modal
    >
      <image-uploader
        :title="'Upload Epic Fail Images'"
        :emitSignal="'epicImageUploaded'"
        @epicImageUploaded="updateEpicFailImage"
      />
    </b-modal>
  </section>
</template>

<script>
import ImageUploader from '@/components/modals/ImageUploader'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { notificationMixin } from '@/mixins/notificationMixin'
import ImageManagement from '@/components/concerns/Images/ImageManagement'

export default {
  props: ['step', 'steppable'],
  mixins: [notificationMixin],
  data () {
    return {
      updateStepErrors: null,

      editingStep: null,

      isHintImageModalActive: false,
      isEpicFailImageModalActive: false
    }
  },
  components: {
    ImageUploader,
    ValidationProvider,
    ValidationObserver,
    ImageManagement
  },
  methods: {
    imagesUpdated (values) {
      this.$emit('imagesUpdated', values)
    },
    resetErrors () {
      this.updateStepErrors = null
    },
    initEditingStep () {
      this.editingStep = {
        title: this.step.attributes.title,
        description: this.step.attributes.description,
        hint: this.step.attributes.hint,
        hint_image: null,
        epic_fail: this.step.attributes.epic_fail,
        epic_fail_image: null
      }
    },
    updateStep () {
      this.$emit('updateStep', this.editingStep)
      this.$parent.close()
    },
    deleteStep () {
      this.$emit('deleteStep')
      this.$parent.close()
    },
    updateEpicFailImage (value) {
      if (value) {
        this.editingStep.epic_fail_image = value
      }
    },
    updateHintImage (value) {
      if (value) {
        this.editingStep.hint_image = value
      }
    },
    deleteEpicFailImage () {
      this.$store
        .dispatch('steps/epic_fail_images/destroy', { stepId: this.step.id })
        .then((response) => {
          this.$emit('stepUpdated', response.data.data)
          this.actionSuccess('Image deleted !')
          this.editingStep.epic_fail_image = null
        })
        .catch((error) => {
          console.log(error.response)
        })
    },
    deleteHintImage () {
      this.$store
        .dispatch('steps/hint_images/destroy', { stepId: this.step.id })
        .then((response) => {
          this.$emit('stepUpdated', response.data.data)
          this.actionSuccess('Image deleted !')
          this.editingStep.hint_image = null
        })
        .catch((error) => {
          console.log(error.response)
        })
    }
  },
  created () {
    this.initEditingStep()
  }
}
</script>

<style lang="css" scoped></style>
